import { Text, TextProps, Tooltip } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { FC, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useAnalytics } from '@/context/Analytics';
import { useResource } from '@/lib/datx-jsonapi-react';
import { queryLiblynxAuthorizationGrants } from '@/queries/liblynx-authorization-grants';
import { sessionQueryOptions } from '@/queries/upgrade/sessions';

interface IInstitutionLabelProps extends TextProps {
  /**
   * Whether this instance reports `library_liblynx_access`.
   *
   * The header renders the label twice — once at the far right for desktop, once
   * inside the drawer below it — and both are always mounted, since only CSS
   * hides one. Exactly one may track, or every page view counts the event twice.
   */
  trackAccess?: boolean;
}

export const InstitutionLabel: FC<IInstitutionLabelProps> = ({ trackAccess = true, ...rest }) => {
  const { t } = useTranslation();
  const { data: session } = useQuery(sessionQueryOptions());
  const user = session?.user;
  const isTrackedRef = useRef(false);

  const analytics = useAnalytics();

  const isPremium = user?.package === 'premium';

  const { data } = useResource(queryLiblynxAuthorizationGrants('auth_grant'), {
    onSuccess: () => {
      if (trackAccess && data?.liblynxOrganization && !isTrackedRef.current) {
        analytics.track('library_liblynx_access', {
          institution_name: data.liblynxOrganization,
        });
        isTrackedRef.current = true;
      }
    },
  });
  const isLiblynxGrantActive = data?.status === 'active';

  if (!data || (!isLiblynxGrantActive && !isPremium)) {
    return null;
  }

  const institutionName = isLiblynxGrantActive ? data.liblynxOrganization : undefined;

  return (
    <Tooltip
      label={institutionName}
      isDisabled={!institutionName}
      maxW={{ base: 'xs', lg: '4xl' }}
      wordBreak="break-word"
    >
      {/*
        tabIndex makes the label focusable so the tooltip is reachable by keyboard,
        not just by pointer. Without it Tab skips straight past and the institution
        name is unreachable.
      */}
      <Text tabIndex={institutionName ? 0 : undefined} {...rest}>
        {t('liblynx.institutionLabel.premium')}
      </Text>
    </Tooltip>
  );
};
